import {useMutation} from '@tanstack/react-query';
import axios from 'axios';
import srtParser2 from 'srt-parser-2';
import assetFetchers from '@/api/fetchers/content-studio/asset';
import projectUpdaters from '@/api/updaters/content-studio/project';
import {Asset} from '@/views/content-studio/components/asset-table/types';
import {
  CONTENT_LAB,
  INITIALIZE_REPHRASING_PROJECT,
} from '@/hooks/content-studio/consts';
import {contentStudioActions} from '@/stores/contentStudioStore';

type InitializeRephraseProjectResponse = {
  downloadUrl: string;
  projectId: string;
};

type InitializeRephraseProjectWithAssetResponse = {
  project: InitializeRephraseProjectResponse;
  asset: Asset;
};

type InitializeRephraseProjectVariables = {
  assetId: string;
  projectName: string;
  analytics_params: {
    interaction_id: string;
    session_id: string;
  };
};

const useInitializeRephrasingProject = () => {
  return useMutation({
    mutationKey: [CONTENT_LAB, INITIALIZE_REPHRASING_PROJECT],
    mutationFn: async ({
      assetId,
      projectName,
      analytics_params,
    }: InitializeRephraseProjectVariables) => {
      const [project, asset] = await Promise.all([
        projectUpdaters.initializeRephraseProject({
          assetId,
          projectName,
          analytics_params,
        }),
        assetFetchers.getAssetById(assetId),
      ]);

      return {
        project,
        asset,
      } as InitializeRephraseProjectWithAssetResponse;
    },
    onSuccess: async (
      data: InitializeRephraseProjectWithAssetResponse,
      variables: InitializeRephraseProjectVariables
    ) => {
      const srt = await axios.get(data.project.downloadUrl);
      // eslint-disable-next-line new-cap
      const srtParser = new srtParser2();
      const parsedSrt = srtParser.fromSrt(srt.data);
      const currentSrt = parsedSrt.map((item) => ({
        ...item,
        currentText: item.text,
        originalText: item.text,
      }));
      contentStudioActions.setCurrentRephraseProject({
        id: data.project.projectId,
        currentSrt,
        assetId: variables.assetId,
        videoUrl: data.asset.url ?? '',
        videoName: data.asset.name ?? '',
        projectName: variables.projectName,
      });
    },
  });
};

export default useInitializeRephrasingProject;
